<template>
	<section ref="sectionRef"
			 class="flex-module container-fluid flexible-related-cases">

		<div class="row">
			<div class="col-xs-12">

				<h2 class="section-title">
					{{ item.title }}
				</h2>
			</div>
		</div>
		<div class="row">
			<div class="col-xs-12 col-md-5 col-lg-4">
				<div class="row">
					<div v-if="item.icon_video && !isMobile"
						 class="col-xs-4 col-sm-4 col-md-12 icon">
						<figure>
							<BaseVideo :data="item.icon_video"
									   preload
									   loop
									   autoplay
									   muted
									   playsinline />
						</figure>
					</div>

					<div class="col-xs-12 col-sm-8 col-md-12 description"
						 v-html="item.description" />
				</div>
			</div>
			<div class="col-xs-12 col-sm-8 col-sm-offset-4 col-md-7 col-md-offset-0 col-lg-6 col-lg-offset-2">

				<NuxtLink v-for="{ work_id: work } in item.works"
						  :key="work.id + work.slug"
						  :to="`/work/${work.slug}`"
						  class="card-link notlink"
						  data-cursor-custom
						  data-cursor-text="PEEP IT">

					<baseCard class="card"
							  :style="`--details-color: ${work.details_color};`"
							  @mouseenter="mouseEnter"
							  @mouseleave="mouseLeave">

						<template #imageSlot>

							<div class="image-wrapper">

								<clientOnly>
									<baseVideo v-if="work.thumbnail_video && !isMobile"
											   :data="work.thumbnail_video"
											   preload
											   loop
											   autoplay
											   muted
											   playsinline />
								</clientOnly>

								<basePicture v-if="getFeaturedCaseImage(work)"
											 class="static-image"
											 :image="getFeaturedCaseImage(work)"
											 :class="{ hidden: hover && !isMobile }"
											 sizes="xs:100vw sm:50vw md:50vw lg:50vw xl:25vw 2xl:25vw 3xl:25vw"
											 loading="eager" />
							</div>
						</template>

						<template #contentSlot>
							<div v-if="work.client"
								 class="card-subtitle">

								<span class="subtitle-description">
									Case:
								</span>
								<span class="subtitle-content">
									{{ work.client }}
								</span>
							</div>
						</template>
					</baseCard>
				</NuxtLink>
			</div>
		</div>
	</section>
</template>

<script setup>
// :style="`padding-top: ${(work.thumbnail_image.data.height / work.thumbnail_image.data.width) * 100}%`"
const props = defineProps({
	data: {
		type: Object,
		required: true,
	},
})

const { gsap } = useGsap()
const mm = gsap.matchMedia()

const sectionRef = ref(null)

const isMobile = useBreakpoint('below', 'md')
const hover = ref(false)
const triggers = {}
let $q
// guards the async play() in mouseEnter — the pointer may have moved to another card,
// or off entirely, before it resolves
let hoveredCard = null
let videos = []

const item = computed(() => props.data?.item)

function moveCases() {
	mm.add('(min-width: 768px)', () => {
		triggers.tl = gsap
			.timeline({
				scrollTrigger: {
					trigger: sectionRef.value,
					start: 'top bottom',
					end: 'bottom top+=' + 0,
					scrub: 1,
				},
			})
			.fromTo($q('.card-link:first-of-type'), {
				y: 50,
			}, {
				y: -50,
			}, 0)
			.fromTo($q('.card-link:last-of-type'), {
				y: -50,
			}, {
				y: 50,
			}, 0)

		return () => {
			// custom cleanup code
		}
	})
}

function initAnims() {
	$q = gsap.utils.selector(sectionRef.value)
	moveCases()
}

// Safari won't autoplay at all in Low Power Mode, and holds a muted video until WebKit
// considers it visible — so a card can sit paused on a black frame. play() stays
// rejected until the page has had a user gesture, hence the swallowed catch.
function ensurePlaying(video) {
	if (!video || !video.paused) return

	video.play().catch(() => { })
}

function onCanPlay(e) {
	ensurePlaying(e.target)
}

function mouseEnter(e) {
	const el = e.currentTarget

	if (isMobile.value) return

	hoveredCard = el

	// the cards are in a v-for, so the video comes from the hovered card itself
	const video = el.querySelector('video')

	// nothing to reveal, or it's already running — behave as before
	if (!video || !video.paused) {
		el.classList.add('hovered')
		return
	}

	// only uncover the video once it is genuinely playing, otherwise the static image
	// fades away to a black frame and a play icon for anyone in Low Power Mode
	video.play()
		.then(() => hoveredCard === el && el.classList.add('hovered'))
		.catch(() => { })
}

function mouseLeave(e) {
	const el = e.currentTarget

	if (hoveredCard === el) hoveredCard = null

	el.classList.remove('hovered')
}

onMounted(() => {
	useAnims(initAnims, triggers)

	// baseVideo sits inside clientOnly, so it isn't in the DOM yet on this tick
	nextTick(() => {
		videos = [...(sectionRef.value?.querySelectorAll('video') || [])]

		videos.forEach((video) => {
			video.addEventListener('canplay', onCanPlay)
			ensurePlaying(video)
		})
	})
})

onBeforeUnmount(() => {
	videos.forEach(video => video.removeEventListener('canplay', onCanPlay))
	videos = []
})
</script>

<style lang="scss" scoped>
.flexible-related-cases {
	overflow: visible;
}

.icon {
	margin-bottom: rem-calc(60);
	max-width: rem-calc(200);
}

.description {
	@include textBody;

	@include for-devices-below(md) {
		margin-bottom: rem-calc(30);
	}
}

.card-link {
	display: inline-block;
	width: calc(50% - $half-gutter-width);
	box-shadow: none;

	&:first-of-type {
		margin-right: $half-gutter-width;
	}

	&:last-of-type {
		margin-left: $half-gutter-width;
	}

	@include for-devices-below(xl) {
		width: 100%;

		&:first-of-type {
			margin-right: 0;
		}

		&:last-of-type {
			margin-left: 0;
		}
	}
}

.card {
	h2 {
		margin: rem-calc(0px 0px 24px 0px);
	}

	.image-wrapper {
		// display: block;
		aspect-ratio: 16 / 9;
		width: 100%;
		position: relative;
		@include rounded-corners;

		video {
			position: absolute;
			top: 50%;
			left: 50%;
			transform: translate(-50%, -50%);
			height: auto;
			min-height: 100%;
			min-width: 100%;
			object-fit: cover;
		}

		picture {
			position: absolute;
			top: 0;
			left: 0;
			width: 100%;
			height: 100%;
			transition: opacity 0.5s $easeOutQuint;

			:deep(img) {
				position: absolute;
				top: 50%;
				left: 0;
				transform: translateY(-50%);
				height: auto;
				min-height: 100%;
				min-width: 100%;
			}
		}
	}

	&.hovered {
		picture {
			opacity: 0;
		}
	}

	.card-subtitle {
		color: var(--details-color, $black);
		margin: rem-calc(24px 0px 0px 0px);
		@include textBody;

		.subtitle-content {
			font-weight: bold;
		}
	}
}
</style>